import { createContext, useContext, useEffect, useRef } from 'react';
import type { Socket } from 'socket.io-client';
import type { ClientToServerEvents, ServerToClientEvents } from '@itsappy/shared';

export type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export interface SocketContextValue {
  socket: AppSocket | null;
  connected: boolean;
}

/**
 * Kept apart from the provider component so the module exports only hooks and
 * values — a file that mixes components with other exports breaks Fast
 * Refresh.
 */
export const SocketContext = createContext<SocketContextValue>({
  socket: null,
  connected: false,
});

export function useSocket(): SocketContextValue {
  return useContext(SocketContext);
}

/**
 * Subscribes to a socket event for the lifetime of a component, keeping the
 * handler reference fresh so callers don't need to memoize it.
 */
export function useSocketEvent<E extends keyof ServerToClientEvents>(
  event: E,
  handler: ServerToClientEvents[E],
): void {
  const { socket } = useSocket();
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => {
    if (!socket) return;
    const listener = ((...args: unknown[]) =>
      (ref.current as (...a: unknown[]) => void)(...args)) as ServerToClientEvents[E];
    socket.on(event, listener as never);
    return () => {
      socket.off(event, listener as never);
    };
  }, [socket, event]);
}
